import type { MouseEvent } from 'react'
import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  Box,
  Flex,
  HStack,
  type HTMLChakraProps,
  MenuRoot,
  MenuSeparator,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  MenuContent as RawMenuContent,
  MenuItem as RawMenuItem,
  MenuPositioner as RawMenuPositioner,
  MenuTrigger as RawMenuTrigger,
  RadioGroupItem as RawRadioGroupItem,
  RadioGroupItemText as RawRadioGroupItemText,
  Spacer,
  TertiaryButton,
  Text,
  Tooltip,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowDownToBracketRegular,
  FaArrowUpFromBracketRegular,
  FaChevronDownRegular,
  FaEllipsisRegular,
  FaGearRegular,
  FaPlusRegular,
  FaSitemapRegular,
  FaTrashCanRegular,
  FaXmarkRegular,
} from '@enechain/ecloud-icons'

import { useLoadedIssuerOptions } from '~/components/bidding/common/IssuerSelector'
import { DsSelect } from '~/components/common/DsSelect'
import { RichActionMenu } from '~/components/common/RichActionMenu'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { iconSize } from '~/constants/iconSize'
import { useCsvImportLinkBuilder } from '~/hooks/useCsvImportReturn'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useWorkflowCaseIdParam } from '~/hooks/workflowCase/useWorkflowCaseIdParam'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { downloadCsv } from '~/libs/downloadCsv'
import { openOrNavigate } from '~/libs/openOrNavigate'
import type { BlockBidCsvKind } from '~/models/bidCsvHeader'
import { AreaFilterSummary } from '~/pages/SpotBidding/components/AreaFilterSummary'
import { BlockBidTable } from '~/pages/SpotBidding/components/BlockBidTable'
import { useSharedBidListFilterItems } from '~/pages/SpotBidding/components/SharedBidListFilterFields'
import { SpotBidMenuItemContent } from '~/pages/SpotBidding/components/SpotBidMenuItemContent'
import { SPOT_LIST_FILTER_STORAGE_KEY } from '~/pages/SpotBidding/constants/listFilter'
import { LIST_HEADER_HEIGHT } from '~/pages/SpotBidding/constants/listHeader'
import { useBidCsvExportContext } from '~/pages/SpotBidding/hooks/useBidCsvExportContext'
import type { BulkWithdrawMenu } from '~/pages/SpotBidding/hooks/useBulkWithdrawControl'
import { useBulkWithdrawMenuView } from '~/pages/SpotBidding/hooks/useBulkWithdrawMenuView'
import type {
  BlockBidTabListFilters,
  BlockKindFilter,
  BlockSubmitFilter,
} from '~/pages/SpotBidding/models/bidListFilters'
import {
  blockBidCsvPriceQueries,
  buildBlockBidCsvFiles,
} from '~/pages/SpotBidding/models/blockBidCsv'
import type {
  BlockBidRow,
  BlockKind,
} from '~/pages/SpotBidding/models/blockBidRow'
import { blockRowBidIssuerType } from '~/pages/SpotBidding/models/blockBidRow'
import { isNonExpiredDraftBlockRow } from '~/pages/SpotBidding/models/blockDraftGroup'
import { resolveBlockClearAllScope } from '~/pages/SpotBidding/models/clearAllPlan'
import type { SpotMarketPhase } from '~/pages/SpotBidding/models/marketPhase'
import { isSpotBeforeDeadline } from '~/pages/SpotBidding/models/marketPhase'
import {
  isAlwaysListedDraft,
  isInPageAreaScope,
  isInPageIssuerScope,
} from '~/pages/SpotBidding/models/pageIssuerScope'
import {
  EMPTY_SHARED_BID_LIST_FILTER,
  type SharedBidListFilter,
  filterBlockGroupsBySharedListFilter,
} from '~/pages/SpotBidding/models/sharedBidListFilter'
import { buildSpotCsvImportPath } from '~/pages/SpotCsvImport/models/spotCsvImportPath'
import { routePaths } from '~/routes/routePaths'

// chakra v3.29 + React 18 types の既知不具合 (d.ts collapse) で RadioGroupItem* の
// children / value が欠落するため cast。
const RadioGroupItem = RawRadioGroupItem as React.FC<
  HTMLChakraProps<'div'> & { value: string }
>
const RadioGroupItemText = RawRadioGroupItemText as React.FC<
  HTMLChakraProps<'span'>
>
const EMPTY_SELECTED_AREAS = new Set<AreaCode>()
const NOOP = (): void => undefined

/**
 * ⋯ メニューは ecloud custom `Menu` ではなく chakra 素の Menu slot で組む
 * (= BlockBidTable / MasterTableActionBar と同じ許可済みパターン)。
 *
 * 理由: ecloud `Menu` の `content` 型は `{ icon, label, value, variant, disabled, onClick }`
 * 固定で **item ごとの `title` (無効理由 tooltip) を渡す口が無い**。 design/CLAUDE.md は
 * 「⋯ 項目の無効理由は `title` (tooltip) に載せる」「無効理由をホバーで読ませたい時は
 * pointer-events を殺さず cursor:not-allowed + title」と定めており (design `spot.html:4549-4553`
 * の BLOCK_MENU_REASONS が実体)、 DS 高レベル部品では表現できない。 raw slot の使用は
 * この ⋯ メニューに封じ込め、 見た目 (negative 項目の色 / divider) は DS token で揃える。
 * `MenuPositioner` を挟むのは positioner 無しだと popup が inline 展開されてヘッダを
 * 押し広げるため (MasterTableActionBar の同コメント参照)。
 *
 * 型は chakra v3 の d.ts collapse (上記 RadioGroupItem と同じ問題) を cast で復元する。
 */
const MenuTrigger = RawMenuTrigger as React.FC<
  HTMLChakraProps<'button'> & { asChild?: boolean }
>
const MenuContent = RawMenuContent as React.FC<HTMLChakraProps<'div'>>
const MenuPositioner = RawMenuPositioner as React.FC<
  HTMLChakraProps<'div'> & { children?: React.ReactNode }
>
const MenuItem = RawMenuItem as React.FC<
  HTMLChakraProps<'div'> & {
    value: string
    disabled?: boolean
    onSelect?: () => void
  }
>

type Props = {
  memberBidDisabledReason?: string
  /**
   * 入札一覧テーブル行データ (= useQueries 並列 Get から convertBlockBidRows で変換済)。
   * エリアフィルタは本コンポーネント内で適用する。
   */
  blockBidRows: BlockBidRow[]
  /**
   * 通常入札タブと共有する売/買・ステータス・ワークフローの絞り込み (AC-BSP-019)。 page が持つ 1 つの値で、
   * 「クリア」 もこの値を戻す。 渡し忘れでタブ内に閉じた絞り込みに戻らないよう必須。
   */
  listFilter: SharedBidListFilter
  onListFilterChange: (next: SharedBidListFilter) => void
  /** ワークフローフィルタの選択肢 (受渡日の通常入札・ブロック入札の札が持つワークフロー ID)。 */
  workflowFilterIds: readonly string[]
  /**
   * このタブ固有の絞り込み (種別・提出ステータス)。 保持 (AC-SC-031) と CSV 取込の着地での戻し方を page が
   * まとめて持つので、 値は page から受け取る。 変える時は変えた項目だけを渡す (page が直前の値に重ねる)。
   */
  tabListFilters: BlockBidTabListFilters
  onTabListFiltersChange: (patch: Partial<BlockBidTabListFilters>) => void
  /** ブロック作成を個別に無効化する理由。指定時は作成ボタンの tooltip に表示する。 */
  createDisabledReason?: string
  /**
   * page-level エリアフィルタ (AC-BSP-019、 複数選択) の実効エリア集合
   * (= 0 件選択は呼び出し元で取引可能な全エリアに正規化済み)。
   */
  selectedAreas: Set<AreaCode>
  /** 未正規化のエリア選択。1 件以上なら一覧内に AC-BSP-132 の状態を示す。 */
  rawSelectedAreas?: Set<AreaCode>
  /** AC-BSP-132 の「絞り込みを解除」。空選択 (= 全エリア) へ戻す。 */
  onClearAreaFilter?: () => void
  /** エリア未選択時の区分不明の札を出せる業務エリア (AC-BSP-111b)。 */
  unknownIssuerUnselectedAreas?: readonly AreaCode[]
  /**
   * 入札一覧をスコープする発注主体区分 (AC-BSP-019 ※)。 ツールバーの区分を page が
   * 求めた値で、 エリアフィルタの可否と同じ。 UNSPECIFIED の間は絞り込まない。
   * 渡し忘れで黙って無スコープになるのを型で防ぐため必須。
   */
  issuerScopeType: LicenseType
  /** page で選択中の受渡日 (= CSV ファイル名に使う)。 */
  targetDate: Date
  /**
   * JEPX スポット市場フェーズ (= 親 page の calcSpotPhase 結果)。
   * GC 締切後は全削除を無効化する (design `spot.html:4549-4553`)。
   */
  phase: SpotMarketPhase
  /** ブロック作成モーダルを開く。 */
  onOpenBlockCreateModal: () => void
  /**
   * 承認状態別のメインアクションボタン (= 承認申請 / 承認申請中 / JEPXに提出)。
   * 親 (index.tsx) が DRAFT/PENDING/APPROVED 件数から出し分けて渡す
   * (= design `spot-bid-action-group` と同形、 NormalBidTab headerActionSlot と同じ位置に配置)。
   *
   * 引数は承認申請・提出の対象 = 一覧に表示中の下書きの group の全 leg (AC-BSP-007)。 いずれかの
   * leg が表示中なら group 全体が対象 (LINK / LOOP のペアは分けられない。 AC-BSP-116 と同じ規則)。
   * 表示中の定義 (下の `filteredBids`) を持つこのタブが渡し、 親は押した時点でこの group を確定する。
   */
  headerActionSlot?: (
    visibleDraftRows: readonly BlockBidRow[],
  ) => React.ReactNode
  /** group 単位の編集トリガー (= DRAFT の親行のみ表示、 issue #2090)。 */
  onEditGroup?: (groupId: string) => void
  /** group 単位の削除トリガー (= DRAFT の親行のみ表示、 issue #2090)。 */
  onDeleteGroup?: (groupId: string) => void
  /** 入札中の group の取下トリガー (AC-BSP-057)。 */
  onWithdrawGroup?: (groupId: string) => void
  /** 送信中の group の「下書きに戻す」 トリガー (AC-BSP-117 / AC-BSP-129)。 */
  onReturnGroupToDraft?: (groupId: string) => void
  /**
   * 行クリックで group を開く (AC-BSP-001a)。 `onEditGroup` と違い締切後 /
   * 読み取り専用でも常に渡す (= 表示専用で開く導線は塞がない。 通常入札
   * `NormalBidTab` の `onOpenBid` と同じ扱い)。 編集可否は開いた側 (親 page) の
   * `BlockBidCreateModal` `readOnly` が group の承認状態から判定する。
   */
  onOpenGroup?: (groupId: string) => void
  /**
   * DRAFT の承認依頼 (group) が 1 件以上あるか。 全削除は DRAFT group のみを対象に
   * するため、 0 件なら ⋯ メニューの「ブロックを全削除」を無効化する (issue #2091)。
   */
  hasDraftGroup?: boolean
  /**
   * 「ブロックを全削除」確認モーダルを開く (issue #2091)。 引数は削除対象の leg (= 押した時点で
   * 一覧に表示中の下書きの group の全 leg)。 確認 modal の件数と削除が同じ group を
   * 指すよう、 対象はここで確定して渡す。
   */
  onOpenClearAllModal?: (targets: readonly BlockBidRow[]) => void
  /**
   * workflow-back-bar-readonly AC-15: ワークフローケースが完了済み/削除済みのとき true。
   * true のときブロック入札の作成・編集・削除・全削除を disabled にする
   * （表示切替系ではなく編集・確定操作のため）。 CSV ダウンロードは読み取り操作なので
   * readonly でも塞がない (design/CLAUDE.md)。
   */
  isReadonly?: boolean
  /** チェック中エリアをコマ別合算した need[] (= GetProcurementNeed 由来)。 */
  needByKoma?: readonly number[]
  /**
   * ⋯ メニュー「入札中の札を一括で取下」 (AC-BSP-116) の状態と押した時の操作。 通常入札・
   * ブロック入札の合算で決まるので親が持つ。 渡さない間は項目を出さない。
   */
  bulkWithdraw?: BulkWithdrawMenu
}

// 種別・提出ステータスの filter の値域は、 保持 (AC-SC-031) と合わせて `bidListFilters.ts` が持つ。
type KindFilter = BlockKindFilter
type SubmitFilter = BlockSubmitFilter

// 行の種別 (LINK-P 等) を親種別グループ (LINK 等) に畳む。
const kindGroupOf = (kind: BlockKind): KindFilter => {
  if (kind.startsWith('LINK')) return 'LINK'
  if (kind.startsWith('LOOP')) return 'LOOP'
  return 'STANDARD'
}

/**
 * 「ブロック作成」split button の CSVインポート項目 (種別ごと)。
 *
 * ブロック入札 CSV は種別で列構成が異なる (通常ブロック = 56 列、 リンクブロック・ループ =
 * 58 列。 spec spot-bid-block-csv-import.md §2)。 取込画面はどの項目から入っても 4 種類の
 * ヘッダーを受け付け、 列構成は CSV のヘッダーで決まる (AC-DSB-013) ので、 `?blockType=` は
 * Step1 の案内とヘッダー不一致のエラーに示す列名にだけ使う。 `blockType` の値域は取込画面側の
 * `BlockBidCsvKind` と同じ型で縛る。
 */
const BLOCK_CSV_IMPORT_MENU_ITEMS: Array<{
  blockType: BlockBidCsvKind
  labelKey: string
  descriptionKey: string
}> = [
  {
    blockType: 'standard',
    labelKey: 'page:biddingBlock.createMenu.csvImportStandard',
    descriptionKey: 'page:biddingBlock.createMenu.csvImportStandardDesc',
  },
  {
    blockType: 'link',
    labelKey: 'page:biddingBlock.createMenu.csvImportLinked',
    descriptionKey: 'page:biddingBlock.createMenu.csvImportLinkedDesc',
  },
  {
    blockType: 'loop',
    labelKey: 'page:biddingBlock.createMenu.csvImportLoop',
    descriptionKey: 'page:biddingBlock.createMenu.csvImportLoopDesc',
  },
]

/**
 * SpotBidding ブロック入札タブ。
 *
 * design HTML `design/bidding/spot.html` `#tab-block` panel を BFF RPC 実データで描画する。
 * 構成:
 * 1. 「ブロック入札とは」 info alert (= all-or-nothing 方式の説明)
 * 2. 入札一覧: 表の直上の開閉式の絞り込み (CollapsibleFilterToolbar) + アクション + 一覧テーブル
 *
 * データフロー: index.tsx が useListBiddingBlockApprovalRequests + useQueries (並列 Get) で
 * BiddingBlockApprovalRequest[] を取得し、 convertBlockBidRows で BlockBidRow[] に変換して
 * blockBidRows prop として渡す。 本コンポーネントはエリア / 種別 / 提出ステータスと、 通常入札タブと
 * 共有する売/買・ステータス (page が持つ `listFilter`) のフィルタを内部で適用する。 ブロックの約定結果は約定結果タブに置き、 本タブには約定結果の表を
 * 置かない (§3 機能詳細 1 / AC-BSP-037)。 約定したかどうかはステータス列で示す。
 *
 * 入札一覧ヘッダの ⋯ メニュー (issue #2091): CSV ダウンロードは
 * 本コンポーネントで完結し、 ブロック全削除だけは group 単位の Delete RPC を持つ親
 * (index.tsx) へ `onOpenClearAllModal` で委譲する。
 */
export const BlockBidTab = ({
  memberBidDisabledReason,
  blockBidRows,
  createDisabledReason,
  listFilter,
  onListFilterChange,
  workflowFilterIds,
  tabListFilters,
  onTabListFiltersChange,
  selectedAreas,
  rawSelectedAreas = EMPTY_SELECTED_AREAS,
  onClearAreaFilter = NOOP,
  unknownIssuerUnselectedAreas,
  issuerScopeType,
  targetDate,
  phase,
  onOpenBlockCreateModal,
  headerActionSlot,
  onEditGroup,
  onDeleteGroup,
  onWithdrawGroup,
  onReturnGroupToDraft,
  onOpenGroup,
  hasDraftGroup = false,
  onOpenClearAllModal,
  isReadonly = false,
  bulkWithdraw,
}: Props): React.ReactElement => {
  const navigate = useNavigate()
  // 取込が終わったら今の画面 (受渡日などの query ごと) へ戻れるよう、 戻り先を付ける
  const csvImportLink = useCsvImportLinkBuilder()
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const caseId = useWorkflowCaseIdParam()
  const { readonlyReason } = useWorkflowLockReasons()

  // 種別・提出ステータスは page が持つ (props の `tabListFilters`)。
  const { blockKind: kindFilter, blockSubmit: submitFilter } = tabListFilters
  const setKindFilter = (next: KindFilter): void => {
    onTabListFiltersChange({ blockKind: next })
  }
  const setSubmitFilter = (next: SubmitFilter): void => {
    onTabListFiltersChange({ blockSubmit: next })
  }

  // 区分を選び直す・作り直すしかない下書きのグループは、 区分とエリアの絞り込みに関わらず出す
  // (AC-BSP-019)。 ループは A か B のどちらかで当てはまればグループごと出す。
  const loadedIssuerOptions = useLoadedIssuerOptions(targetDate)
  const alwaysListedGroupIds = useMemo(() => {
    const rowsByGroup = new Map<string, BlockBidRow[]>()
    for (const row of blockBidRows) {
      rowsByGroup.set(row.groupId, [
        ...(rowsByGroup.get(row.groupId) ?? []),
        row,
      ])
    }
    const groupIds = new Set<string>()
    for (const [groupId, rows] of rowsByGroup) {
      const isAlwaysListed = isAlwaysListedDraft(
        {
          isDraft: rows[0].approvalState === BiddingApprovalState.DRAFT,
          issuerType: blockRowBidIssuerType(rows[0]),
          areas: rows.map((r) => r.areaCode),
        },
        loadedIssuerOptions,
      )
      if (isAlwaysListed) groupIds.add(groupId)
    }
    return groupIds
  }, [blockBidRows, loadedIssuerOptions])

  // 入札締切前かどうか (= NormalBidTab の isBeforeDeadline と同じ判定。 スポットの
  // 締切は「GC」ではなく「入札締切」と表記する — AC-BSP-064 / docs/domain-terms.md)。
  const isBeforeDeadline = isSpotBeforeDeadline(phase)

  // エリア + 発注主体区分 + 種別 + 提出ステータス + 共有の売/買・ステータス フィルタ適用後の入札一覧。
  // 売/買は Leg ごとに違いうるので、 どの条件も同じ Leg で判定し、 合う Leg が 1 本でもあるグループは
  // 全 Leg を出す (AC-BSP-019)。 提出ステータスは approvalState で判定: SUBMITTED = 提出済 / それ以外 = 未提出。
  const filteredBids = useMemo(
    () =>
      filterBlockGroupsBySharedListFilter(
        blockBidRows,
        listFilter,
        !isBeforeDeadline,
        (b) => {
          const isAlwaysListed = alwaysListedGroupIds.has(b.groupId)
          if (
            !isAlwaysListed &&
            !(
              isInPageAreaScope(
                b.areaCode,
                blockRowBidIssuerType(b),
                selectedAreas,
                unknownIssuerUnselectedAreas,
              ) &&
              isInPageIssuerScope(blockRowBidIssuerType(b), issuerScopeType)
            )
          )
            return false
          if (kindFilter !== '' && kindGroupOf(b.kind) !== kindFilter)
            return false
          if (
            submitFilter === 'submitted' &&
            b.approvalState !== BiddingApprovalState.SUBMITTED
          )
            return false
          if (
            submitFilter === 'unsubmitted' &&
            b.approvalState === BiddingApprovalState.SUBMITTED
          )
            return false
          return true
        },
      ),
    [
      blockBidRows,
      listFilter,
      isBeforeDeadline,
      alwaysListedGroupIds,
      selectedAreas,
      unknownIssuerUnselectedAreas,
      issuerScopeType,
      kindFilter,
      submitFilter,
    ],
  )

  // 承認申請・直接提出の対象 (AC-BSP-007)。 表示中の定義は上の filteredBids が唯一の SoT。
  // いずれかの leg が表示中なら group 全体 (LINK / LOOP のペアは分けられない。 AC-BSP-116 と同じ規則)。
  // 入札締切まで提出せず失効にした group は下書きに数えない (AC-BSP-099)。
  const visibleDraftRows = useMemo(() => {
    const groupIds = new Set(
      filteredBids.filter(isNonExpiredDraftBlockRow).map((b) => b.groupId),
    )
    return blockBidRows.filter((b) => groupIds.has(b.groupId))
  }, [blockBidRows, filteredBids])

  // 入札後ポジション chart / 充足率バッジは **タブの外** (`SpotPositionChartPanel`)
  // へ移した (2026-09-07 / issue #6596 論点3)。 チャートは通常＋ブロックの合算 1 本で、
  // 合算 slot は page が組んで直接そのパネルへ渡すため、 本タブは chart 用の
  // 派生値 (own slot / coverage / badgeColor) を一切持たない。

  // 開閉式フィルターの件数 (既定から変えている一覧固有の条件。 AC-BSP-132)。 画面の最上部の
  // 共通ツールバー (受渡日・エリア・発注主体区分) の値は数えず、 クリアでも戻さない。
  // 共有の売/買・ステータスは通常入札タブにも効く (AC-BSP-019)。 種別・提出ステータスはこのタブだけ。
  const sharedFilter = useSharedBidListFilterItems(
    listFilter,
    onListFilterChange,
    workflowFilterIds,
  )
  const modifiedFilterCount =
    (kindFilter !== '' ? 1 : 0) +
    (submitFilter !== '' ? 1 : 0) +
    sharedFilter.modifiedCount
  const resetFilters = (): void => {
    onListFilterChange(EMPTY_SHARED_BID_LIST_FILTER)
    setKindFilter('')
    setSubmitFilter('')
  }
  const submitFilterOptions: Array<{ value: SubmitFilter; label: string }> = [
    { value: '', label: t('common:filter.all') },
    {
      value: 'submitted',
      label: t('page:biddingSpot.approvalState.submitted'),
    },
    {
      value: 'unsubmitted',
      label: t('page:biddingSpot.bidStatus.notSubmitted'),
    },
  ]
  const kindFilterOptions: Array<{ value: KindFilter; label: string }> = [
    { value: '', label: t('common:filter.all') },
    {
      value: 'STANDARD',
      label: t('page:biddingSpot.blockExec.kind.standard'),
    },
    { value: 'LINK', label: t('page:biddingSpot.blockExec.kind.link') },
    { value: 'LOOP', label: t('page:biddingSpot.blockExec.kind.loop') },
  ]
  // 要約は既定でも「すべて」と出す (空白にしない)
  const filterSummary: ToolbarStatusItem[] = [
    {
      key: 'submitStatus',
      label: t('page:biddingSpot.sidebar.filterSubmitStatus'),
      value:
        submitFilterOptions.find((o) => o.value === submitFilter)?.label ??
        t('common:filter.all'),
    },
    {
      key: 'kind',
      label: t('page:biddingSpot.column.kind'),
      value:
        kindFilterOptions.find((o) => o.value === kindFilter)?.label ??
        t('common:filter.all'),
    },
    // 共有の売/買・ステータス (AC-BSP-019) は design の既存の並び (提出ステータス → 種別) の後
    ...sharedFilter.summary,
  ]
  const filterGridItems: FilterGridItem[] = [
    {
      key: 'submitStatus',
      label: t('page:biddingSpot.sidebar.filterSubmitStatus'),
      // 提出ステータス (= すべて / 提出済 / 未提出)。
      control: (
        <RadioGroupRoot
          onValueChange={(d: { value: string | null }) =>
            setSubmitFilter((d.value ?? '') as SubmitFilter)
          }
          value={submitFilter}
        >
          <HStack gap={3}>
            {submitFilterOptions.map((o) => (
              <RadioGroupItem key={o.value} value={o.value}>
                <RadioGroupItemHiddenInput />
                <RadioGroupItemControl />
                <RadioGroupItemText fontSize="md">{o.label}</RadioGroupItemText>
              </RadioGroupItem>
            ))}
          </HStack>
        </RadioGroupRoot>
      ),
    },
    {
      key: 'kind',
      label: t('page:biddingSpot.column.kind'),
      // 種別 (= すべて / 通常 / リンク / ループ)。 単一 select の正は DS の `Select`
      // (2026-08-05 デザイナー確定)。 見出しは `<label for>` を張らないので読み上げ名を付ける。
      control: (
        <DsSelect
          aria-label={t('page:biddingSpot.column.kind')}
          onChange={(v) => setKindFilter((v ?? '') as KindFilter)}
          options={kindFilterOptions}
          value={kindFilter}
        />
      ),
    },
    ...sharedFilter.gridItems,
  ]

  // ===== ⋯ メニュー (CSV ダウンロード / ブロックを全削除) issue #2091 =====
  // ブロックを作成できるか (= NormalBidTab の canCreateBid と同型)。 締切前
  // (AC-BSP-064) かつワークフローケースが読み取り専用でないときのみ許可する。
  const canCreateBlock =
    isBeforeDeadline &&
    !isReadonly &&
    createDisabledReason === undefined &&
    memberBidDisabledReason === undefined
  // ブロックが 1 件でもあるか (全削除の「0 件」理由用)。 CSV と全削除の対象はどちらも
  // 表示中の一覧 (= NormalBidTab の allBids / filteredBids と同じ切り分け)。
  const hasAnyBlock = blockBidRows.length > 0
  const hasVisibleBlock = filteredBids.length > 0

  // 無効化条件と理由 tooltip (design `spot.html:4549-4553` BLOCK_MENU_REASONS)。
  // 「読み取り専用 (workflow-back-bar-readonly AC-15)」は design に無い実装側の条件で、
  // 通常入札の入札追加と同じく、 読み取り専用の理由 (readonlyReason) を最優先で出す。
  // CSV ダウンロードは読み取り操作なので readonly / GC 締切後でも塞がない (design/CLAUDE.md)。
  // CSV は手数料自動考慮が ON の間、 保存済みの入札価格から手数料単価で逆算した希望価格を
  // 出す (機能詳細 9)。 ON で手数料単価を取得できない間 (AC-BSP-100) と、 手数料自動考慮の
  // 設定・会社情報の取得に失敗した間は出力できず、 押せない理由を項目の下に文で出す
  // (ツールチップだけにしない)。 設定・会社情報を取得している間は押せるままにし、 押された
  // 出力は届くまで待つ (useBidCsvExportContext)。
  const csvExport = useBidCsvExportContext()
  const csvDownloadDisabled =
    !hasVisibleBlock || csvExport.blockedReason !== undefined
  // 全削除の対象 = 一覧に表示中の下書きの group の全 leg (機能詳細 7)。
  const clearAllTargets = useMemo(
    () => resolveBlockClearAllScope(blockBidRows, filteredBids),
    [blockBidRows, filteredBids],
  )
  // 一括取下 (AC-BSP-116) の項目。 対象は page が共通ツールバーの範囲と両タブで共有する絞り込みから
  // 数える (種別・提出ステータスは掛けない) ので、 このタブは表示だけを持つ。
  const bulkWithdrawView = useBulkWithdrawMenuView(bulkWithdraw)
  const clearAllDisabled =
    isReadonly ||
    !isBeforeDeadline ||
    !hasAnyBlock ||
    !hasDraftGroup ||
    clearAllTargets.length === 0

  const csvDownloadTitle = (): string => {
    if (csvExport.blockedReason !== undefined) return csvExport.blockedReason
    return hasVisibleBlock
      ? t('page:biddingBlock.menuTooltip.csvDownloadActive')
      : t('page:biddingBlock.menuTooltip.csvDownloadEmpty')
  }
  // ワークフロー読み取り専用を画面固有の理由 (承認フロー設定未取得・明細未解決・締切後) より
  // 先に単独で出す (AC-WFW-014)。 `isReadonly` は親から明細未解決も含めて渡るので、
  // 理由の有無は readonlyReason (ワークフロー取得済みで完了・削除済み) で判定する。
  // 理由が無い (押せる) 時は undefined。
  const createButtonTooltip: string | undefined =
    memberBidDisabledReason ??
    readonlyReason ??
    createDisabledReason ??
    (isBeforeDeadline
      ? undefined
      : t('page:biddingBlock.createButton.deadlineTooltip'))
  const clearAllTitle = (): string => {
    // 完了・削除済みの読み取り専用の理由を、他の理由より先に単独で出す (AC-WFW-014)。
    // 取得中・失敗中や明細未解決で isReadonly になる時は readonlyReason が無く、
    // 締切後・空・下書きなしの理由を従来どおり出す。
    if (readonlyReason !== undefined) return readonlyReason
    if (!isBeforeDeadline)
      return t('page:biddingBlock.menuTooltip.clearAllPost')
    if (!hasAnyBlock) return t('page:biddingBlock.menuTooltip.clearAllEmpty')
    if (!hasDraftGroup)
      return t('page:biddingBlock.menuTooltip.clearAllNoDraft')
    if (clearAllTargets.length === 0)
      return t('page:biddingBlock.menuTooltip.clearAllNoVisibleDraft')
    return t('page:biddingBlock.menuTooltip.clearAllActive')
  }

  // 現在表示中 (= フィルタ適用後) のブロック一覧を client 側で CSV 化してダウンロードする。
  // export RPC は作らず、 表示中データから直接生成する (= 通常入札の CSV と同方針)。
  // 取込と同じ列 + 末尾のステータス列で、 取込の種別 (通常ブロック / リンクブロック / ループ) は
  // 1 ファイルに混ぜられないため、 札がある種別の分だけファイルを出す (機能詳細 9)。
  // 出すのは押した時点で表示中の札 (取得を待つ間に一覧が変わっても、 押した時の内容を出す)。
  const handleDownloadCsv = (): void => {
    csvExport.requestExport({
      priceQueries: blockBidCsvPriceQueries(filteredBids),
      run: (inputs) => {
        const files = buildBlockBidCsvFiles(filteredBids, {
          ...inputs,
          deliveryDate: targetDate,
          // ステータス列 (AC-BSP-049 の 11 区分) は画面 (BlockBidTable) と同じ
          // isPastDeadline (= !isBeforeDeadline) で導出する。
          isPastDeadline: !isBeforeDeadline,
        })
        for (const file of files) {
          downloadCsv(file.filename, file.csv)
        }
        showSuccessToast({
          title: t('page:biddingBlock.csvDownload.success', {
            count: filteredBids.length,
          }),
        })
      },
    })
  }

  return (
    <VStack align="stretch" gap={4}>
      {/* ========== 入札一覧 ==========
          フィルターは一覧のカードの中・表のすぐ上の開閉式 (UI 辞書 toolbar-collapsible.md「開閉式」。
          2026-10-27 ユーザー指示。 通常入札タブと同じ)。 左の縦のサイドバー列 (FilterRail 等) は廃止した。 */}
      <Box minWidth={0}>
        {/* ===== 入札一覧 accordion ===== */}
        {/* ===== 入札一覧 =====
            **アコーディオンにしない** (デザイナー指摘 2026-08-27「ブロック入札 > 入札一覧も
            同様」。 通常入札は #6644 で対応済み)。 8080 `bidding/spot.html:690` のコメント
            「入札一覧 / ブロック入札 は常時開きっぱなしで運用、 アコーディオン chevron は不要
            + 下線をフィルター見出しと統一」 が根拠。 8080 が `<details>` の器を残しているのは
            既存の grid / CSS を壊さないためで、 振る舞いは常時開いた見出し + 本文。
            DS の Accordion は chevron と開閉が必ず付いてくるので器ごと素の Box へ置き換える。
            見出しの余白と中の導線のサイズは通常入札 (NormalBidTab) に揃える
            (デザイナー指摘 2026-08-27)。 */}
        <Box
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="md"
          backgroundColor="Surface.primary"
        >
          <Flex
            alignItems="center"
            flexShrink={0}
            gap={3}
            // 区切り線は見出し側に持つ (8080 の summary の border-bottom と同じ)。
            // 常時開いているので「閉じている間も線が残る」 問題は起きない。
            height={LIST_HEADER_HEIGHT}
            borderBottomWidth="1px"
            // 高さは 56px (= 通常入札タブと同じ理由。 NormalBidTab の同 Flex のコメント参照)。
            borderBottomColor="Border.lowEmphasis"
            paddingX={4}
            paddingY={2}
          >
            <HStack flex={1} gap={3} width="100%" minWidth={0}>
              {/* 「{エリア} に絞り込み中」 は見出しの右 16px (AC-BSP-132。 2026-11-02 デザイナー指示
                    「見出しの下じゃなくて、 見出しから 16px 開けて右側に配置」)。 */}
              <HStack gap={4} minWidth={0}>
                <Text
                  textStyle="sectionTitle"
                  flexShrink={0}
                  whiteSpace="nowrap"
                >
                  {t('page:biddingSpot.list.bidListTitle')}{' '}
                  <Text
                    as="span"
                    textStyle="captionNormal"
                    color="Text.midEmphasis"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {t('page:biddingSpot.list.countSuffix', {
                      count: filteredBids.length,
                    })}
                  </Text>
                </Text>
                <AreaFilterSummary
                  onClear={onClearAreaFilter}
                  selectedAreas={rawSelectedAreas}
                />
              </HStack>
              <Spacer />
              {/* アクション群: ブロック作成 / 提出 / ⋯。 accordion trigger 内なので
                    click 伝播を止めて開閉と干渉させない。 */}
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                <HStack gap={2}>
                  {/* 「ブロック作成」は split button (spec spot-bid-block-csv-import.md §6):
                        個別作成モーダル / CSVインポート子ページ の導線を持つ。
                        通常入札タブの「入札追加」と同型。

                        CSV は 3 種別で列構成が異なる (通常ブロック = 56 列、
                        リンクブロック・ループ = 58 列。 spec §2) ため、 種別ごとに
                        メニュー項目を分けて `?blockType=` を渡す。 取込画面はどの
                        項目から入っても 4 種類のヘッダーを受け付ける (AC-DSB-013)。

                        入札締切後 (AC-BSP-064) と読み取り専用のときは trigger を disabled にし、
                        理由は hover tooltip で示す (通常入札タブの「入札追加」 と同形。 非表示に
                        すると導線ごと消えて迷子になるため disabled 表示)。
                        issue #5349: 締切後もここが活性で、 モーダルまで進めていた。 */}
                  {canCreateBlock ? (
                    /* design `spot.html` の `.ecl-split-btn` 準拠。 **主ボタン =
                         個別作成を即実行**、 右の caret からその他の作成方法
                         (CSV 3 種別) を開く。 2 つのボタンの間に区切り線を入れる
                         (= design の `.ecl-split-btn-main` / `.ecl-split-btn-caret`
                         の境界)。 通常入札タブの「入札追加」 と同型にする
                         (2026-09-01 デザイナー指摘「ブロック作成も区切り線ありの
                         split button に」。 それまでは 4 項目を対等に並べる
                         dropdown で、 通常入札側とだけ形が違っていた)。
                         区切り線は caret の左 border (文字色の 25%) + `marginLeft="-1px"` で主ボタンの右 border に
                         重ねて 1 本にする (design/ui-feasibility/components/split-button.md「dev 実装の現状」。
                         計画作成 = pages/ProcurementPlanList と同じ書き方。 文字色から作るので variant ごとに色を書かない)。 */
                    <HStack gap={0}>
                      <TertiaryButton
                        borderRightRadius={0}
                        onClick={() => onOpenBlockCreateModal()}
                        size="md"
                      >
                        <FaPlusRegular boxSize={iconSize.labeledButton} />
                        {t('page:biddingBlock.createButton.label')}
                      </TertiaryButton>
                      <RichActionMenu
                        ariaLabel={t('page:biddingBlock.createMenu.moreLabel')}
                        items={BLOCK_CSV_IMPORT_MENU_ITEMS.map((item) => ({
                          value: `csv-${item.blockType}`,
                          title: t(item.labelKey),
                          description: t(item.descriptionKey),
                          icon: (
                            <FaArrowUpFromBracketRegular
                              boxSize={iconSize.labeledButton}
                            />
                          ),
                          onClick: () => {
                            void navigate(
                              csvImportLink(
                                buildSpotCsvImportPath(
                                  { kind: 'block', blockType: item.blockType },
                                  caseId,
                                ),
                              ),
                            )
                          },
                        }))}
                        trigger={
                          <TertiaryButton
                            marginLeft="-1px"
                            borderLeftWidth="1px"
                            borderLeftColor="color-mix(in srgb, currentColor 25%, transparent)"
                            borderLeftRadius={0}
                            aria-label={t(
                              'page:biddingBlock.createMenu.moreLabel',
                            )}
                            paddingX={2}
                            size="md"
                            title={t('page:biddingBlock.createMenu.moreLabel')}
                          >
                            {/* caret は装飾 (開閉の読み上げ名は aria-label 側が持つ)。
                              8080 は 20px の svg chevron (`.ecl-split-btn-caret svg`、10/19 実測)。
                              [DS差異] design/CLAUDE.md の「caret は Solid」は
                              `@enechain/ecloud-icons` に FaChevronDownSolid が無いため
                              満たせず、 Regular を採る (MasterTableActionBar と同判断)。 */}
                            <FaChevronDownRegular
                              boxSize={iconSize.labeledButton}
                            />
                          </TertiaryButton>
                        }
                      />
                    </HStack>
                  ) : (
                    <Tooltip
                      content={createButtonTooltip ?? ''}
                      isHidden={
                        createButtonTooltip === undefined ||
                        createButtonTooltip === ''
                      }
                      placement="top"
                      showArrow
                    >
                      {/* disabled 要素は pointer event を発火しないため span で包んで
                            hover を拾う (NormalBidTab の入札追加と同パターン)。 */}
                      <span>
                        {/* **器は活性時と同じ 2 枚**にする。 1 枚に畳んで caret を
                            文字で入れると、 締切をまたいだ瞬間に区切り線が消えて
                            別部品に変わったように見える (design は
                            `.ecl-split-btn.is-disabled` で 2 枚を残したまま
                            両方を薄くする)。 2026-09-01 の split button 化で
                            畳んでしまっていたのを是正。

                            hover を拾う外側が `span` (インライン) なので、 器も
                            `span` の inline-flex にする。 既定の `div` だと
                            インライン要素の中にブロック要素が入り、 外側 `span` の
                            矩形がボタン群とずれて **tooltip が離れた位置に出る**。 */}
                        <HStack as="span" gap={0} display="inline-flex">
                          <TertiaryButton
                            borderRightRadius={0}
                            disabled
                            size="md"
                          >
                            <FaPlusRegular boxSize={iconSize.labeledButton} />
                            {t('page:biddingBlock.createButton.label')}
                          </TertiaryButton>
                          <TertiaryButton
                            marginLeft="-1px"
                            borderLeftWidth="1px"
                            borderLeftColor="color-mix(in srgb, currentColor 25%, transparent)"
                            borderLeftRadius={0}
                            aria-label={t(
                              'page:biddingBlock.createMenu.moreLabel',
                            )}
                            disabled
                            paddingX={2}
                            size="md"
                          >
                            {/* caret は装飾。 読み上げ名は aria-label が担う。 */}
                            <FaChevronDownRegular
                              boxSize={iconSize.labeledButton}
                            />
                          </TertiaryButton>
                        </HStack>
                      </span>
                    </Tooltip>
                  )}
                  {/* 承認状態別メインアクション (= 承認申請 / 承認申請中 / JEPXに提出)。
                        design `spot-bid-action-group` 同様、 通常入札タブと同じ位置に配置する。 */}
                  {headerActionSlot?.(visibleDraftRows)}
                  {/* ⋯ (= design `block-more-menu`)。 popup は positioner + portal で
                        overlay 表示する。 無効項目は pointer-events を殺さず `title` で
                        理由を読ませる (design/CLAUDE.md「disabled 表示」)。 */}
                  <MenuRoot>
                    {/* トリガーの見た目は design/CLAUDE.md「⋯（kebab）メニュー」節に従う:
                        `btn-tertiary btn-square` (= 枠あり) + DS〈FA 由来〉の水平3点。
                        枠なしの Text variant と、 アイコンではなく文字リテラルの `⋯` を
                        使っていたのを是正 (2026-08-31 デザイナー指摘)。 枠なしの
                        `ecl-icon-btn--sm` は**テーブル行内のみ**の例外で、 ここは
                        ヘッダーなので対象外。 trigger 自体が button になるため、 中は
                        `as="span"` で見た目だけ借りる (button の入れ子を作らない)。 */}
                    <MenuTrigger
                      aria-label={t('page:biddingBlock.menu.moreLabel')}
                      title={t('page:biddingBlock.menu.moreLabel')}
                    >
                      {/* icon だけのボタンは 40×40 の正方形 (UI 辞書
                          dictionary/components.md §1 / 8080 `btn-square`)。 */}
                      <TertiaryButton
                        as="span"
                        width="40px"
                        minWidth="40px"
                        paddingX={0}
                        size="md"
                      >
                        <FaEllipsisRegular boxSize={iconSize.labeledButton} />
                      </TertiaryButton>
                    </MenuTrigger>
                    <MenuPositioner>
                      <MenuContent minWidth="240px">
                        <MenuItem
                          onClick={(e: MouseEvent) =>
                            openOrNavigate(
                              navigate,
                              routePaths.adminBidLimits.path,
                              e,
                            )
                          }
                          value="bid-limits"
                        >
                          <SpotBidMenuItemContent
                            Icon={FaGearRegular}
                            label={t('page:biddingBlock.menu.bidLimits')}
                          />
                        </MenuItem>
                        <MenuItem
                          onClick={(e: MouseEvent) =>
                            openOrNavigate(
                              navigate,
                              routePaths.adminApprovalFlow.path,
                              e,
                            )
                          }
                          value="approval-flow"
                        >
                          {/* [8080 差異] 8080 は丸チェックだが、 丸チェックの
                              Regular は lint で禁止 (状態アイコンは Solid) で、
                              Solid にすると「成功状態」に読める。 承認フローの
                              構造を表す sitemap へ寄せる。 */}
                          <SpotBidMenuItemContent
                            Icon={FaSitemapRegular}
                            label={t('page:biddingBlock.menu.approvalFlow')}
                          />
                        </MenuItem>
                        <MenuSeparator />
                        <MenuItem
                          disabled={csvDownloadDisabled}
                          onSelect={handleDownloadCsv}
                          title={csvDownloadTitle()}
                          value="csv-export"
                        >
                          <SpotBidMenuItemContent
                            Icon={FaArrowDownToBracketRegular}
                            description={csvExport.blockedReason}
                            label={t('page:biddingBlock.menu.csvDownload')}
                          />
                        </MenuItem>
                        {/* 入札中の札を一括で取下 (AC-BSP-116)。 下書きを消す全削除とは別の操作なので、
                            CSV と全削除の間に区切り線で別グループにする (design spot.html:1853)。
                            対象は通常入札とブロック入札の合算で、 どちらのタブから押しても同じ。 */}
                        {bulkWithdraw !== undefined &&
                          bulkWithdrawView !== undefined && (
                            <>
                              <MenuSeparator />
                              <MenuItem
                                disabled={bulkWithdrawView.disabled}
                                onSelect={bulkWithdraw.onOpen}
                                title={bulkWithdrawView.title}
                                value="bulk-withdraw"
                              >
                                <SpotBidMenuItemContent
                                  Icon={FaXmarkRegular}
                                  description={bulkWithdrawView.description}
                                  label={t(
                                    'page:biddingSpot.bulkWithdraw.menu',
                                  )}
                                />
                              </MenuItem>
                            </>
                          )}
                        <MenuSeparator />
                        <MenuItem
                          color="Text.negativeHigh"
                          disabled={clearAllDisabled}
                          onSelect={() =>
                            onOpenClearAllModal?.(clearAllTargets)
                          }
                          title={clearAllTitle()}
                          value="clear-all"
                        >
                          <SpotBidMenuItemContent
                            Icon={FaTrashCanRegular}
                            label={t('page:biddingBlock.menu.clearAll')}
                          />
                        </MenuItem>
                      </MenuContent>
                    </MenuPositioner>
                  </MenuRoot>
                </HStack>
              </Box>
            </HStack>
          </Flex>
          {/* 上罫線は見出し側の下線に一本化した (二重線を避ける) */}
          <Box padding={0}>
            {/* ========== 入札一覧の絞り込み (開閉式) ==========
                表のすぐ上。 外側のカードが枠を持つので枠を付けず (`framed={false}`)、 表との間は
                区切り線にする。 件数・クリアの対象は一覧固有の条件 (提出ステータス・種別) だけ。
                共通ツールバーの値は数えず、 クリアでも戻さない (AC-BSP-132)。 */}
            <Box borderBottomWidth="1px" borderBottomColor="Border.lowEmphasis">
              <CollapsibleFilterToolbar
                framed={false}
                modifiedCount={modifiedFilterCount}
                onClear={resetFilters}
                storageKey={SPOT_LIST_FILTER_STORAGE_KEY}
                summary={filterSummary}
              >
                <FilterGrid items={filterGridItems} />
              </CollapsibleFilterToolbar>
            </Box>

            {/* 手順ステップ表示は 2026-10-05 の PdM 判断で廃止した (#8312、 通常入札と同じ)。
                承認の状態はブロック入札の承認状態バナー、 札ごとの進み具合はステータス列で読める。 */}
            <BlockBidTable
              /* 締切後 (AC-BSP-064) と読み取り専用では handler を渡さず、 行の
                   編集 / 削除メニューを disabled にする (issue #5349)。 */
              isPastDeadline={!isBeforeDeadline}
              isWorkflowReadonly={isReadonly}
              onDeleteGroup={
                isReadonly || !isBeforeDeadline ? undefined : onDeleteGroup
              }
              // ステータス列の 11 区分導出 (AC-BSP-049) に渡す締切通過フラグ。
              onEditGroup={
                isReadonly || !isBeforeDeadline ? undefined : onEditGroup
              }
              // 行クリック / 詳細メニューは締切後・読み取り専用でも常に渡す
              // (AC-BSP-001a「締切後も表示専用で開く」)。
              onOpenGroup={onOpenGroup}
              // 締切・読み取り専用の判定は canWithdrawBlockBidGroup が見る。
              onReturnGroupToDraft={onReturnGroupToDraft}
              // 同じく canReturnToDraft が見る。
              onWithdrawGroup={onWithdrawGroup}
              rows={filteredBids}
            />
          </Box>
        </Box>
      </Box>
    </VStack>
  )
}
